import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Plus, Edit2, Save, AlertTriangle, ArrowUpRight, ArrowDownRight,
  DollarSign, Ban
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Financials = ({ invoices, onRefresh }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [bills, setBills] = useState([]);
  const [badDebts, setBadDebts] = useState([]);
  const [pnlReport, setPnlReport] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [billForm, setBillForm] = useState({
    vendor_name: "", description: "", amount: "", category: "other", due_date: "", status: "pending", notes: ""
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value || 0);
  };

  const fetchFinancialData = useCallback(async () => {
    try {
      const [billsRes, badDebtRes, summaryRes, pnlRes] = await Promise.all([
        axios.get(`${API}/bills`),
        axios.get(`${API}/bad-debt`),
        axios.get(`${API}/financial/summary`),
        axios.get(`${API}/financial/profit-loss`)
      ]);
      setBills(billsRes.data);
      setBadDebts(badDebtRes.data);
      setFinancialSummary(summaryRes.data);
      setPnlReport(pnlRes.data);
    } catch (error) {
      console.error("Error fetching financial data:", error);
    }
  }, []);

  useEffect(() => {
    fetchFinancialData();
  }, [fetchFinancialData]);

  const handleOpenBillDialog = (bill = null) => {
    if (bill) {
      setEditingBill(bill);
      setBillForm({
        vendor_name: bill.vendor_name || "",
        description: bill.description || "",
        amount: bill.amount?.toString() || "",
        category: bill.category || "other",
        due_date: bill.due_date ? new Date(bill.due_date).toISOString().split('T')[0] : "",
        status: bill.status || "pending",
        notes: bill.notes || ""
      });
    } else {
      setEditingBill(null);
      setBillForm({ vendor_name: "", description: "", amount: "", category: "other", due_date: "", status: "pending", notes: "" });
    }
    setBillDialogOpen(true);
  };

  const handleSaveBill = async () => {
    try {
      const payload = {
        ...billForm,
        amount: parseFloat(billForm.amount) || 0,
        due_date: billForm.due_date ? new Date(billForm.due_date).toISOString() : null
      };
      if (editingBill) {
        await axios.put(`${API}/bills/${editingBill.id}`, payload);
      } else {
        await axios.post(`${API}/bills`, payload);
      }
      setBillDialogOpen(false);
      fetchFinancialData();
    } catch (error) {
      console.error("Error saving bill:", error);
    }
  };

  const handleWriteOffBadDebt = async (invoiceId) => {
    const reason = window.prompt("Enter reason for writing off this invoice as bad debt:");
    if (reason !== null) {
      try {
        await axios.post(`${API}/bad-debt?source_type=invoice&source_id=${invoiceId}&reason=${encodeURIComponent(reason)}`);
        fetchFinancialData();
        onRefresh();
      } catch (error) {
        console.error("Error writing off bad debt:", error);
      }
    }
  };

  const overdueInvoices = invoices.filter(inv => inv.status === 'overdue');
  const overdueBills = bills.filter(b => b.status === 'overdue');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="financials-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Financials</h2>
          <p className="text-slate-500 font-manrope">Track revenue, expenses, and profitability</p>
        </div>
        <Button onClick={() => handleOpenBillDialog()} className="bg-royal-blue-700 hover:bg-royal-blue-800">
          <Plus size={18} className="mr-2" /> Add Bill/Expense
        </Button>
      </div>

      {financialSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Revenue</p>
                  <p className="font-cabinet text-2xl font-bold text-green-700">{formatCurrency(financialSummary.revenue?.total)}</p>
                  <p className="text-xs text-green-600">Pending: {formatCurrency(financialSummary.revenue?.pending)}</p>
                </div>
                <ArrowUpRight className="text-green-600" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">Total Expenses</p>
                  <p className="font-cabinet text-2xl font-bold text-orange-700">{formatCurrency(financialSummary.expenses?.total)}</p>
                  <p className="text-xs text-orange-600">Pending: {formatCurrency(financialSummary.expenses?.pending)}</p>
                </div>
                <ArrowDownRight className="text-orange-600" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Bad Debt</p>
                  <p className="font-cabinet text-2xl font-bold text-red-700">{formatCurrency(financialSummary.bad_debt?.total)}</p>
                  <p className="text-xs text-red-600">From invoices: {formatCurrency(financialSummary.bad_debt?.from_invoices)}</p>
                </div>
                <Ban className="text-red-600" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-royal-blue-200 bg-royal-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-royal-blue-600 font-medium">Net Position</p>
                  <p className={`font-cabinet text-2xl font-bold ${financialSummary.net_position >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(financialSummary.net_position)}
                  </p>
                  <p className="text-xs text-royal-blue-600">Revenue - Expenses - Bad Debt</p>
                </div>
                <DollarSign className="text-royal-blue-600" size={24} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="bills">Bills & Expenses</TabsTrigger>
          <TabsTrigger value="bad-debt">Bad Debt</TabsTrigger>
          <TabsTrigger value="pnl">P&L Statement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle size={18} /> Overdue Invoices ({overdueInvoices.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {overdueInvoices.slice(0, 5).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div>
                        <p className="font-medium">{inv.invoice_number}</p>
                        <p className="text-sm text-slate-500">{new Date(inv.due_date).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-red-600">{formatCurrency(inv.total)}</p>
                        <Button size="sm" variant="ghost" className="text-xs text-red-600" onClick={() => handleWriteOffBadDebt(inv.id)}>
                          Write Off
                        </Button>
                      </div>
                    </div>
                  ))}
                  {overdueInvoices.length === 0 && <p className="text-center text-slate-500 py-4">No overdue invoices</p>}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-600">
                  <AlertTriangle size={18} /> Overdue Bills ({overdueBills.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {overdueBills.slice(0, 5).map(bill => (
                    <div key={bill.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div>
                        <p className="font-medium">{bill.vendor_name}</p>
                        <p className="text-sm text-slate-500">{bill.description}</p>
                      </div>
                      <p className="font-bold text-orange-600">{formatCurrency(bill.amount)}</p>
                    </div>
                  ))}
                  {overdueBills.length === 0 && <p className="text-center text-slate-500 py-4">No overdue bills</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="bills" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium">Bill #</th>
                    <th className="text-left p-4 font-medium">Vendor</th>
                    <th className="text-left p-4 font-medium">Description</th>
                    <th className="text-left p-4 font-medium">Category</th>
                    <th className="text-left p-4 font-medium">Amount</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map(bill => (
                    <tr key={bill.id} className="border-b hover:bg-slate-50">
                      <td className="p-4 font-medium">{bill.bill_number}</td>
                      <td className="p-4">{bill.vendor_name}</td>
                      <td className="p-4 text-sm text-slate-600">{bill.description}</td>
                      <td className="p-4"><Badge variant="outline">{bill.category}</Badge></td>
                      <td className="p-4 font-semibold">{formatCurrency(bill.amount)}</td>
                      <td className="p-4">
                        <Badge className={
                          bill.status === 'paid' ? 'bg-green-100 text-green-600' :
                          bill.status === 'overdue' ? 'bg-red-100 text-red-600' :
                          'bg-yellow-100 text-yellow-600'
                        }>{bill.status}</Badge>
                      </td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenBillDialog(bill)}>
                          <Edit2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bills.length === 0 && <p className="text-center py-8 text-slate-500">No bills recorded</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bad-debt" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium">Source</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Amount</th>
                    <th className="text-left p-4 font-medium">Reason</th>
                    <th className="text-left p-4 font-medium">Written Off Date</th>
                  </tr>
                </thead>
                <tbody>
                  {badDebts.map(bd => (
                    <tr key={bd.id} className="border-b hover:bg-slate-50">
                      <td className="p-4 font-medium">{bd.source_number}</td>
                      <td className="p-4"><Badge variant="outline">{bd.type}</Badge></td>
                      <td className="p-4 font-semibold text-red-600">{formatCurrency(bd.amount)}</td>
                      <td className="p-4 text-sm text-slate-600">{bd.reason || '-'}</td>
                      <td className="p-4 text-sm">{new Date(bd.written_off_date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {badDebts.length === 0 && <p className="text-center py-8 text-slate-500">No bad debt recorded</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pnl" className="mt-4">
          {pnlReport && (
            <Card>
              <CardHeader>
                <CardTitle>Profit & Loss Statement</CardTitle>
                <CardDescription>Period: {pnlReport.period_start} to {pnlReport.period_end}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-green-700 mb-3">REVENUE</h3>
                  <div className="space-y-2 pl-4">
                    <div className="flex justify-between"><span>Service Revenue</span><span>{formatCurrency(pnlReport.service_revenue)}</span></div>
                    <div className="flex justify-between"><span>Product Revenue</span><span>{formatCurrency(pnlReport.product_revenue)}</span></div>
                    <div className="flex justify-between"><span>Other Revenue</span><span>{formatCurrency(pnlReport.other_revenue)}</span></div>
                    <Separator />
                    <div className="flex justify-between font-bold"><span>Total Revenue</span><span className="text-green-700">{formatCurrency(pnlReport.total_revenue)}</span></div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-orange-700 mb-3">EXPENSES</h3>
                  <div className="space-y-2 pl-4">
                    {Object.entries(pnlReport.expenses_by_category || {}).map(([cat, amount]) => (
                      <div key={cat} className="flex justify-between"><span className="capitalize">{cat.replace('_', ' ')}</span><span>{formatCurrency(amount)}</span></div>
                    ))}
                    {Object.keys(pnlReport.expenses_by_category || {}).length === 0 && (
                      <p className="text-slate-500 text-sm">No expenses recorded</p>
                    )}
                    <Separator />
                    <div className="flex justify-between font-bold"><span>Total Expenses</span><span className="text-orange-700">{formatCurrency(pnlReport.total_expenses)}</span></div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-red-700 mb-3">BAD DEBT</h3>
                  <div className="space-y-2 pl-4">
                    <div className="flex justify-between"><span>From Invoices</span><span>{formatCurrency(pnlReport.bad_debt_invoices)}</span></div>
                    <div className="flex justify-between"><span>From Bills</span><span>{formatCurrency(pnlReport.bad_debt_bills)}</span></div>
                    <Separator />
                    <div className="flex justify-between font-bold"><span>Total Bad Debt</span><span className="text-red-700">{formatCurrency(pnlReport.total_bad_debt)}</span></div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="space-y-3">
                  <div className="flex justify-between text-lg"><span className="font-semibold">Gross Profit</span><span className={pnlReport.gross_profit >= 0 ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>{formatCurrency(pnlReport.gross_profit)}</span></div>
                  <div className="flex justify-between text-xl bg-slate-100 p-4 rounded-lg"><span className="font-bold">NET PROFIT</span><span className={`font-bold ${pnlReport.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(pnlReport.net_profit)}</span></div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={billDialogOpen} onOpenChange={setBillDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBill ? 'Edit Bill' : 'Add Bill/Expense'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Name</Label>
                <Input value={billForm.vendor_name} onChange={e => setBillForm({...billForm, vendor_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" value={billForm.amount} onChange={e => setBillForm({...billForm, amount: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={billForm.description} onChange={e => setBillForm({...billForm, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={billForm.category} onValueChange={v => setBillForm({...billForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operating">Operating</SelectItem>
                    <SelectItem value="payroll">Payroll</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="utilities">Utilities</SelectItem>
                    <SelectItem value="professional_services">Professional Services</SelectItem>
                    <SelectItem value="referral_fees">Referral Fees</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={billForm.status} onValueChange={v => setBillForm({...billForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={billForm.due_date} onChange={e => setBillForm({...billForm, due_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={billForm.notes} onChange={e => setBillForm({...billForm, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveBill} className="bg-royal-blue-700 hover:bg-royal-blue-800">
              <Save size={16} className="mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
